import { CreateBankingAccount, CreateBankingAccountParticipant, IPrePartner } from 'common/create-banking-account.dto';
import { IAccount } from 'common/accounts.interface';
import React, { FC, useEffect, useState } from 'react';
import { Alert, Box, Typography, CircularProgress } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSnackbar } from 'notistack';
import { AccountService, BankingService, CarrierService } from 'services';
import { useNavigate } from 'react-router';
import { camelToSnakeCase } from 'utils/api';
import StepHeader from './components/StepHeader';
import StepProgress from './components/StepProgress';
import Step1CompanyForm from './components/Step1CompanyForm';
import PartnerFinish from './components/PartnerFinish';

export type CompanyValues = {
  cnpj: string;
  tradingName: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  revenue: string;
  addressPostalCode: string;
  addressState: string;
  addressCity: string;
  addressNeighborhood: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
};

const initialCompanyValues: CompanyValues = {
  cnpj: '',
  tradingName: '',
  businessName: '',
  email: '',
  phoneNumber: '',
  addressPostalCode: '',
  addressState: '',
  addressCity: '',
  addressNeighborhood: '',
  addressStreet: '',
  addressNumber: '',
  addressComplement: '',
  revenue: '',
};

type Props = {
  /** Admin mode: open the account on behalf of this carrier branch instead of the logged user's. */
  carrierBranchId?: number;
  onCreated?: (account: IAccount) => void;
};

const AccountRegisterPage: FC<Props> = ({ carrierBranchId, onCreated }) => {
  const isAdminMode = Boolean(carrierBranchId);

  const bankingService = new BankingService();
  const accountService = new AccountService();
  const carrierService = new CarrierService();

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(1);
  const [companyValues, setCompanyValues] = useState<CompanyValues>(initialCompanyValues);
  const [companyAlert, setCompanyAlert] = useState<React.ReactNode | null>(null);
  const [companyLoading, setCompanyLoading] = useState<boolean>(true);
  const [companyWarning, setCompanyWarning] = useState<string | null>(null);

  const [partners, setPartners] = useState<IPrePartner[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Prefill company data from carrier branch
  useEffect(() => {
    (async () => {
      setCompanyLoading(true);
      try {
        let branchId = carrierBranchId;
        let rootId: number | undefined;

        if (branchId) {
          rootId = (await carrierService.getCarrierBranch(branchId)).carrierRootId;
        } else {
          const account = await accountService.getMe();
          branchId = account.carrierBranchId;
          rootId = account.carrierRootId;
        }

        if (branchId && rootId) {
          const branch = await carrierService.getCarrierBranch(branchId);
          const root = await carrierService.getCarrierRoot(rootId);

          // The account belongs to the branch: in admin mode prefer its own data, falling back to the root's
          const company = isAdminMode
            ? {
                tradingName: branch.tradeName || root.tradeName,
                businessName: branch.corporateName || root.companyName,
                email: branch.email || root.email,
              }
            : { tradingName: root.tradeName, businessName: root.companyName, email: root.email };

          const missingFields = [
            !branch.documentNumber && 'CNPJ',
            !company.tradingName && 'nome comercial',
            !company.businessName && 'razão social',
            !company.email && 'e-mail',
          ].filter(Boolean);

          if (missingFields.length && !isAdminMode) {
            setCompanyAlert(<span>O cadastro da empresa está incompleto, por favor entre em contato com o suporte operacional.</span>);
            setCompanyLoading(false);
            return;
          }

          // Admins can complete the missing data directly in the form
          setCompanyWarning(
            missingFields.length
              ? `O cadastro da transportadora não possui ${missingFields.join(', ')}. Preencha no formulário abaixo.`
              : null
          );

          setCompanyValues((prev) => ({
            ...prev,
            cnpj: branch.documentNumber,
            tradingName: company.tradingName || '',
            businessName: company.businessName || '',
            email: company.email || '',
          }));
        }
      } catch (_e) {
        // noop
      } finally {
        setCompanyLoading(false);
      }
    })();
  }, [carrierBranchId]);

  const onNextStep = (vals: CompanyValues) => {
    setCompanyValues(vals);
    setStep(2);
  };

  const onFinish = async (participants: CreateBankingAccountParticipant[]) => {
    setSubmitting(true);

    try {
      const payload: CreateBankingAccount = {
        cnpj: companyValues.cnpj,
        tradingName: companyValues.tradingName,
        businessName: companyValues.businessName,
        phoneNumber: companyValues.phoneNumber,
        email: companyValues.email,
        addressPostalCode: companyValues.addressPostalCode,
        addressStreet: companyValues.addressStreet,
        addressNumber: companyValues.addressNumber,
        addressComplement: companyValues.addressComplement,
        addressNeighborhood: companyValues.addressNeighborhood,
        addressCity: companyValues.addressCity,
        addressState: companyValues.addressState,
        revenue: companyValues.revenue,
        participants: camelToSnakeCase(participants),
      };

      if (carrierBranchId) {
        const account = await bankingService.createAccountForCarrierBranch(carrierBranchId, payload);
        enqueueSnackbar('Solicitação de abertura de conta enviada.', { variant: 'success' });
        onCreated?.(account);
        return;
      }

      await bankingService.createAccount(payload);

      navigate('/banking');
    } catch (err: any) {
      if (isAdminMode) {
        const detail = err?.response?.data?.detail;
        enqueueSnackbar(typeof detail === 'string' ? detail : 'Ocorreu um erro ao solicitar a conta bancária.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Ocorreu um erro ao solicitar sua conta bancária. Contate o suporte.', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={isAdminMode ? undefined : { px: { md: 12 }, py: { md: 6 } }}>
      <Box display="flex" flexDirection="column">
        {!isAdminMode && (
          <Typography variant="h2" sx={{ fontSize: '32px', fontWeight: 700 }}>
            Abertura da conta digital
          </Typography>
        )}

        {companyAlert ? (
          <Box
            mt={4}
            mb={4}
            p={3}
            bgcolor="#fff3cd"
            border="1px solid #ffeeba"
            borderRadius={2}
            color="#856404"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <WarningAmberIcon sx={{ fontSize: 32 }} />
            {companyAlert}
          </Box>
        ) : (
          <>
            {companyLoading && (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                <CircularProgress />
              </Box>
            )}

            {!companyLoading && companyWarning && step === 1 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                {companyWarning}
              </Alert>
            )}
            {!companyLoading && <StepHeader step={step} />}
            {!companyLoading && <StepProgress step={step} />}

            {step === 1 && !companyLoading && (
              <Step1CompanyForm initialValues={companyValues} partners={partners} setPartners={setPartners} onNext={onNextStep} />
            )}

            {step !== 1 && (
              <PartnerFinish
                partners={partners}
                onBack={() => setStep(1)}
                onFinish={onFinish}
                onSyncPartners={setPartners}
                submitting={submitting}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AccountRegisterPage;
